/* eslint-disable @typescript-eslint/no-unused-vars */
import { VoidCallback } from '@deqo/foundation';
import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { LFC } from '../../Legacy';
import { Icon } from '../../concepts/resources/Icon';
import { Point } from '../../platform/geom/Point';
import { Colors, toRem } from './../../ui';

const StatusFrame = styled.div`
    position: absolute;
    bottom: 0rem;
    right: 0rem;
    z-index: 1;

    svg {
        width: 100%;
    height: 100%;
    position: relative;
    }
`;

const IndicatorOnline = styled.div<{ size: number }>`
    width: ${p => toRem(p.size * 0.35)};
    height: ${p => toRem(p.size * 0.35)};
    background: ${Colors.positive};
    border-radius: 50%;
    border: solid .125rem white;
`;

const IndicatorBoard = ({ size }: { size: number }) => {
    const style = {
        width: toRem(size * 0.4),
        height: toRem(size * 0.4 * 0.9375),
        zIndex: 100,
    };
    return <div style={style}>
        <Icon src="deqo://icons/dash/board-indicator" />
    </div>;
};

const Status: LFC<{ status: string, size: number }> = ({ status, size, children }) => {
    const position = new Point(1, 1).normalize().multiply(size);
    const style = {
        left: toRem(position.x),
        top: toRem(position.y),
    };
    return <StatusFrame style={style}>
        {children}
        {status === 'online' && <IndicatorOnline size={size} />}
        {status === 'board' && <IndicatorBoard size={size} />}
        {/* {status === 'offline' && <CounterIndicatorOffline />} */}
    </StatusFrame>;
};

const Frame = styled.div<{ size: number, border?: string, clickable?: boolean, selected?: boolean, variant?: string }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: relative;
    width: ${p => toRem(p.size)};
    min-width: ${p => toRem(p.size)};
    height: ${p => toRem(p.size)};
    min-height: ${p => toRem(p.size)};
    margin: 1px;
    font-size: ${p => toRem(p.size * .4375)};
    font-weight: 500;
    text-align: center;
    ${p => p.variant === 'square' && 'border-radius: .125rem;'};
    ${p => p.variant === 'roundish' && 'border-radius: .375rem;'};
    ${p => (p.variant === 'round' || !p.variant) && 'border-radius: 9999px;'};
    color: white;
    text-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
    box-shadow: 0 0 0 2px ${p => p.border ?? '#fff'};
    user-select: none;
    background-size: cover;
    white-space: nowrap;

    ${p => p.selected && css`
        z-index: 1;
    `}

    ${p => (!p.border && !p.clickable) ? '' : css`
        &:after {
            position: absolute;
            top: -.3125rem;
            left: -.3125rem;
            right: -.3125rem;
            bottom: -.3125rem;
            content: '';
            border: none;
            border-radius: 50%;
        }
    `}
    
    ${p => !p.clickable ? '' : css`

        &:hover:after { 
            cursor: pointer;
            border: solid .1875rem rgba(0, 0, 0, 0.3);
        }

        &:active:after {
            background: rgba(0, 0, 0, 0.3);
        }
    `}

    ${p => p.selected && css`

        &:after { 
            cursor: pointer;
            border: solid .1875rem ${Colors.primary} !important;
        }
    `}

    &.--offline {
        opacity: 0.3;
    }

    svg, img {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 100%;
        height: 100%;
        transform: translate(-50%, -50%);
    }
`;

const toInitials = (name?: string) => {
    if (!name || !name.length) return '';
    if (name.match(/^\+?\d+$/)) return name;
    if (name.length === 2) return name;
    const [first, last] = name.toUpperCase().split(' ').map(x => x.trim());
    let i = (first[0] || '');
    if (!!last) i += last[0];
    return i;
};

export interface AvatarProps {
    clickable?: boolean;
    size?: string;
    name?: string;
    color?: string;
    photo?: string;
    status?: string;
    border?: string;
    variant?: 'square' | 'roundish' | 'round';
    selected?: boolean;
    onClick?: VoidCallback;
    children?: React.ReactNode;
}

const Base = (props: AvatarProps) => {
    const initials = useMemo(() => toInitials(props.name), [props.name]);
    const { color, photo, status, children, size, ...rest } = props;
    const normSize = parseFloat(props.size) || 2.5;

    const iconPhoto = photo?.startsWith('deqo://icon');

    const style = {
        backgroundImage: (!!photo && photo.startsWith('http')) ? `url(${photo})` : '',
        backgroundColor: color,
    };

    return <Frame {...{ size: normSize, style, ...rest }}>
        {!photo && (children || <span>{initials}</span>)}
        {status && <Status size={normSize} status={status} />}
        {iconPhoto && <Icon src={photo} style={{ transform: 'translate(-50%, -50%) scale(0.75)' }} />}
    </Frame>;
};

const Group = styled.div`
    ${Frame} {
        margin-left: -.25rem;
        vertical-align: middle;

        &:first-child {
            margin-left: 0;
        }

        &:hover {
            position: relative;
            z-index: 2;
        }
    }
`;

export type AvatarUserProps = Pick<AvatarProps, 'name' | 'color' | 'photo' | 'status'>;
export type ForUserAvatarProps = Omit<AvatarProps, keyof AvatarUserProps> & { user: AvatarUserProps };

export const ForUnknown = (props: AvatarProps) => {
    return <Base name="Unknown" {...props} color={Colors.primaryLight}>?</Base>;
};

export const ForUser = ({ user, ...props }: ForUserAvatarProps) => {
    if (!!user) {
        return <Base {...props} name={user.name} color={user.color} photo={user.photo} status={user.status} />;
    }
    else {
        return <ForUnknown {...props} />;
    }
};

export const Avatar = Object.assign(Base, { Group, ForUser, ForUnknown });
